import { FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare } from "lucide-react";
import { api, type DocChatMessage, type DocProposal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  documentId: string;
  proposalActive?: boolean;
  disabled?: boolean;
  onProposalChange?: (proposal: DocProposal | null | undefined) => void;
  onDocumentBody?: (bodyMd: string) => void;
  /** Flush dirty body before chat so model sees latest text. */
  flushBeforeSend?: () => Promise<void>;
};

export default function DocChatPanel({
  documentId,
  proposalActive,
  disabled,
  onProposalChange,
  onDocumentBody,
  flushBeforeSend,
}: Props) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const chatQ = useQuery({
    queryKey: ["docs-chat", documentId],
    queryFn: () => api.docsChatList(documentId),
    enabled: Boolean(documentId),
  });

  const messages: DocChatMessage[] = chatQ.data?.messages || [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, chatQ.isFetching]);

  const chatM = useMutation({
    mutationFn: async (message: string) => {
      if (flushBeforeSend) await flushBeforeSend();
      return api.docsChat(documentId, { message });
    },
    onSuccess: (res) => {
      qc.setQueryData(["docs-chat", documentId], { messages: res.messages });
      if (res.document) {
        qc.setQueryData(["docs-doc", documentId], { document: res.document });
        if (typeof res.document.body_md === "string" && onDocumentBody) {
          // body unchanged by chat itself unless proposal accepted elsewhere
        }
      }
      onProposalChange?.(res.proposal ?? res.document?.proposal ?? null);
    },
  });

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border bg-white">
      <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-medium">
        <MessageSquare className="h-4 w-4 text-indigo-500" />
        Chat o dokumencie
        {proposalActive ? (
          <span className="ml-auto text-[10px] font-normal text-amber-700">propozycja aktywna</span>
        ) : null}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
        {messages.length === 0 ? (
          <p className="text-xs text-slate-500">
            Zapytaj o treść albo poproś o zmianę (np. „Skróć punkty”, „Dodaj CTA na końcu”).
            Edycje pojawią się jako kolorowany podgląd do zatwierdzenia.
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                "rounded-lg px-2.5 py-2 text-xs leading-relaxed",
                m.role === "user" ? "bg-indigo-50 text-indigo-950" : "bg-slate-50 text-slate-800",
              )}
            >
              <div className="mb-0.5 text-[10px] uppercase text-slate-400">
                {m.role === "user" ? "Ty" : "Asystent"}
              </div>
              <div className="whitespace-pre-wrap">{m.content}</div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <form
        className="border-t p-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const msg = input.trim();
          if (!msg || chatM.isPending || disabled) return;
          setInput("");
          chatM.mutate(msg);
        }}
      >
        <textarea
          className="mb-2 w-full rounded-md border px-2 py-1.5 text-sm disabled:opacity-60"
          rows={3}
          value={input}
          disabled={disabled || chatM.isPending}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Zmień treść albo zadaj pytanie…"
        />
        <Button
          type="submit"
          className="w-full"
          disabled={disabled || chatM.isPending || !input.trim()}
        >
          {chatM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Wyślij"}
        </Button>
        {chatM.isError ? (
          <p className="mt-1 text-[11px] text-red-600">
            {chatM.error instanceof Error ? chatM.error.message : "Błąd chatu"}
          </p>
        ) : null}
      </form>
    </aside>
  );
}
